import React from "react";
import { createPortal } from "react-dom";
import { type SortDescriptor } from "@/hooks/useSortPanel";
import Icon from "@mdi/react";
import {
  mdiChevronUp,
  mdiClose,
  mdiArrowUp,
  mdiArrowDown,
  mdiSortAscending,
  mdiSortDescending,
} from "@mdi/js";
import { Button, cn } from "@heroui/react";

// ─── SortableColumnHeader ───

export function SortableColumnHeader({
  children,
  sortDirection,
}: {
  children: React.ReactNode;
  sortDirection?: "ascending" | "descending";
}) {
  return (
    <span className="scd-sortable-header">
      {children}
      {!!sortDirection && (
        <Icon
          path={mdiChevronUp}
          size={0.55}
          className={cn(
            "scd-sortable-header__icon",
            sortDirection === "descending" &&
              "scd-sortable-header__icon--desc",
          )}
        />
      )}
    </span>
  );
}

// ─── SortPanel component ───

export function SortPanel({
  columns,
  sortDescriptor,
  setSortDescriptor,
  isSortOpen,
  setIsSortOpen,
  sortAnchorRef,
  sortPanelRef,
  testIdPrefix = "contractor-detail",
}: {
  columns: { id: string; label: string }[];
  sortDescriptor: SortDescriptor;
  setSortDescriptor: (d: SortDescriptor) => void;
  isSortOpen: boolean;
  setIsSortOpen: (v: boolean) => void;
  sortAnchorRef: React.RefObject<HTMLDivElement | null>;
  sortPanelRef: React.RefObject<HTMLDivElement | null>;
  testIdPrefix?: string;
}) {
  return (
    <>
      <div className="scd-sort-anchor" ref={sortAnchorRef}>
        <Button
          isIconOnly
          variant="tertiary"
          aria-label="Сортировка"
          onPress={() => setIsSortOpen(!isSortOpen)}
          className={cn("icon-btn--card", isSortOpen && "filter-btn--active")}
          data-testid={`${testIdPrefix}-sort-button`}
        >
          <Icon
            path={
              sortDescriptor.direction === "descending"
                ? mdiSortDescending
                : mdiSortAscending
            }
            size={0.85}
          />
        </Button>
      </div>

      {isSortOpen &&
        createPortal(
          <div className="scd-sort-panel" ref={sortPanelRef}>
            <div className="scd-sort-panel__header">
              <span className="scd-sort-panel__title">Сортировка</span>
              <Button
                isIconOnly
                variant="ghost"
                size="sm"
                aria-label="Закрыть сортировку"
                onPress={() => setIsSortOpen(false)}
              >
                <Icon path={mdiClose} size={0.7} />
              </Button>
            </div>

            <div className="scd-sort-panel__body">
              {columns.map((col) => {
                const isActive = sortDescriptor.column === col.id;
                return (
                  <div
                    key={col.id}
                    className={cn(
                      "scd-sort-panel__item",
                      isActive && "scd-sort-panel__item--active",
                    )}
                  >
                    <span className="scd-sort-panel__item-label">
                      {col.label}
                    </span>
                    <div className="scd-sort-panel__item-actions">
                      <Button
                        isIconOnly
                        size="sm"
                        variant={
                          isActive && sortDescriptor.direction === "ascending"
                            ? "primary"
                            : "tertiary"
                        }
                        aria-label={`${col.label} по возрастанию`}
                        onPress={() =>
                          setSortDescriptor({
                            column: col.id,
                            direction: "ascending",
                          })
                        }
                      >
                        <Icon path={mdiArrowUp} size={0.65} />
                      </Button>
                      <Button
                        isIconOnly
                        size="sm"
                        variant={
                          isActive && sortDescriptor.direction === "descending"
                            ? "primary"
                            : "tertiary"
                        }
                        aria-label={`${col.label} по убыванию`}
                        onPress={() =>
                          setSortDescriptor({
                            column: col.id,
                            direction: "descending",
                          })
                        }
                      >
                        <Icon path={mdiArrowDown} size={0.65} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
